import React, { useRef } from 'react';
import { Link } from 'react-router-dom';

import {
  AppStoreBadge,
  Footer,
  Nav,
  Phone,
  QrProvider,
  Shot,
  SkipLink,
  images,
  usePageChrome,
  useReveal,
} from './longevity/shared';
import { PAGE_PATH, PRIVACY_PATH, content } from './longevity/content.mjs';
import { LONGEVITY_HERO_SIZES as HERO_SIZES } from './images/site-sizes.mjs';

const PHONE_SIZES = '(min-width: 900px) 360px, 70vw';
const WIDGET_SIZES = '(min-width: 640px) 240px, 80vw';

function FeatureRow({ row }) {
  const hasImage = Boolean(images[row.image]);
  return (
    <article
      className={`lc-feature lc-reveal${hasImage ? '' : ' lc-feature--text-only'}`}
      id={row.id}
    >
      <div className="lc-feature-text">
        <span className="lc-label">{row.label}</span>
        <h3>{row.heading}</h3>
        {row.intro && <p className="lc-lede">{row.intro}</p>}
        {row.paragraphs &&
          row.paragraphs.map((p) => (
            <p key={p} className="lc-muted">
              {p}
            </p>
          ))}
        {row.points && (
          <ul className="lc-points">
            {row.points.map((point) => (
              <li key={point.title}>
                <strong>{point.title}</strong>
                <span>{point.body}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {hasImage && (
        <div className="lc-feature-media">
          {row.framed ? (
            <Phone name={row.image} alt={row.imageAlt} sizes={PHONE_SIZES} />
          ) : (
            <div className="lc-shot-card">
              <Shot
                name={row.image}
                alt={row.imageAlt}
                sizes="(min-width: 900px) 420px, 90vw"
              />
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function LongevityPage() {
  usePageChrome({
    title: content.meta.title,
    description: content.meta.description,
    path: PAGE_PATH,
  });
  const rootRef = useRef(null);
  useReveal(rootRef);

  const { hero, howItWorks, features, widgets, also, why, privacy, finalCta } =
    content;
  const widgetCards = widgets.cards.filter((card) => images[card.image]);
  const wideWidget =
    widgets.wide && images[widgets.wide.image] ? widgets.wide : null;

  return (
    <div className="lc-page" id="lc-top" ref={rootRef}>
      <QrProvider>
        <SkipLink />
        <Nav home />

        <main id="lc-main">
          <section
            className="lc-hero lc-section"
            id="top"
            aria-labelledby="lc-hero-heading"
          >
            <div className="lc-container lc-hero-grid">
              <div className="lc-hero-text">
                <h1 id="lc-hero-heading">{content.promise}</h1>
                <p className="lc-lede">{hero.supporting}</p>
                <AppStoreBadge />
              </div>
              <div className="lc-hero-phone">
                <Phone
                  name="daily"
                  alt={hero.imageAlt}
                  sizes={HERO_SIZES}
                  priority
                />
              </div>
            </div>
          </section>

          <section
            className="lc-section"
            id="how"
            aria-labelledby="lc-how-heading"
          >
            <div className="lc-container">
              <div className="lc-section-head lc-reveal">
                <span className="lc-label">{howItWorks.label}</span>
                <h2 id="lc-how-heading">{howItWorks.heading}</h2>
              </div>
              <ol className="lc-steps lc-reveal">
                {howItWorks.steps.map((step) => (
                  <li className="lc-step" key={step.title}>
                    <div>
                      <h3>{step.title}</h3>
                      <p>{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <section
            className="lc-section lc-section--surface"
            id="features"
            aria-labelledby="lc-features-heading"
          >
            <div className="lc-container">
              <div className="lc-section-head lc-reveal">
                <span className="lc-label">{features.label}</span>
                <h2 id="lc-features-heading">{features.heading}</h2>
              </div>
              <div className="lc-features">
                {features.rows.map((row) => (
                  <FeatureRow row={row} key={row.id} />
                ))}
              </div>
            </div>
          </section>

          <section
            className="lc-section"
            id="widgets"
            aria-labelledby="lc-widgets-heading"
          >
            <div className="lc-container">
              <div className="lc-section-head lc-section-head--center lc-reveal">
                <span className="lc-label">{widgets.label}</span>
                <h2 id="lc-widgets-heading">{widgets.heading}</h2>
                <p className="lc-lede">{widgets.body}</p>
              </div>
              {widgetCards.length > 0 && (
                <div className="lc-widgets lc-reveal">
                  {widgetCards.map((card) => (
                    <figure className="lc-widget-card" key={card.image}>
                      <Shot
                        name={card.image}
                        alt={card.alt}
                        sizes={WIDGET_SIZES}
                      />
                      <figcaption>{card.caption}</figcaption>
                    </figure>
                  ))}
                </div>
              )}
              {wideWidget && (
                <figure className="lc-widget-wide lc-reveal">
                  <Shot
                    name={wideWidget.image}
                    alt={wideWidget.alt}
                    sizes="(min-width: 640px) 520px, 90vw"
                  />
                  <figcaption>{wideWidget.caption}</figcaption>
                </figure>
              )}
              <p className="lc-widgets-note">{widgets.lockScreenNote}</p>
            </div>
          </section>

          <section
            className="lc-section lc-section--surface"
            id="also"
            aria-labelledby="lc-also-heading"
          >
            <div className="lc-container">
              <div className="lc-section-head lc-reveal">
                <span className="lc-label">{also.label}</span>
                <h2 id="lc-also-heading">{also.heading}</h2>
              </div>
              <div className="lc-cards lc-reveal">
                {also.cards.map((card) => (
                  <article className="lc-card" key={card.label}>
                    <span className="lc-label">{card.label}</span>
                    <h3>{card.heading}</h3>
                    <p>{card.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section
            className="lc-section"
            id="why"
            aria-labelledby="lc-why-heading"
          >
            <div className="lc-container">
              <div className="lc-section-head lc-reveal">
                <span className="lc-label">{why.label}</span>
                <h2 id="lc-why-heading">{why.heading}</h2>
              </div>
              <ul className="lc-facts lc-reveal">
                {why.facts.map((fact) => (
                  <li key={fact}>{fact}</li>
                ))}
              </ul>
              <p className="lc-facts-note lc-reveal">{why.note}</p>
            </div>
          </section>

          <section
            className="lc-section lc-section--surface"
            id="privacy"
            aria-labelledby="lc-privacy-heading"
          >
            <div className="lc-container lc-narrow lc-reveal">
              <span className="lc-label">{privacy.label}</span>
              <h2 id="lc-privacy-heading">{privacy.heading}</h2>
              <p className="lc-lede">{privacy.body}</p>
              <Link className="lc-inline-link" to={PRIVACY_PATH}>
                {privacy.link}
              </Link>
            </div>
          </section>

          <section
            className="lc-section lc-cta"
            id="download"
            aria-labelledby="lc-cta-heading"
          >
            <div className="lc-container lc-narrow lc-reveal">
              <span className="lc-label">{finalCta.label}</span>
              <h2 id="lc-cta-heading">{content.promise}</h2>
              <AppStoreBadge />
              <p className="lc-muted">{finalCta.supporting}</p>
            </div>
          </section>
        </main>

        <Footer />
      </QrProvider>
    </div>
  );
}
